import { useEffect, useState } from "react";
import { getNews } from "../api/common";
import { OIL_DATA_API_KEY } from "../config";

// Mirrors ordr-flutter lib/market_updates (Alpha Vantage BRENT/WTI + v1/common/news)
// and ordrx_frontend's SideBar. Alpha Vantage's free tier allows only a handful of
// calls per day, so results are cached in localStorage and shared between the
// sidebar and the Market Updates page.
const CACHE_TTL = 6 * 60 * 60 * 1000;
const NEWS_CACHE_KEY = "BUYER_MARKET_NEWS";
const memory = {};

function readCache(key) {
	try {
		const raw = JSON.parse(localStorage.getItem(key) || "null");
		if (raw && Date.now() - raw.at < CACHE_TTL) return raw.data;
	} catch {
		// unreadable cache — refetch
	}
	return null;
}

function writeCache(key, data) {
	try {
		localStorage.setItem(key, JSON.stringify({ at: Date.now(), data }));
	} catch {
		// storage full / blocked — the in-memory copy is still used
	}
}

async function fetchSeries(fn, interval) {
	const key = `BUYER_OIL_${fn}_${interval}`;
	if (memory[key]) return memory[key];
	const cached = readCache(key);
	if (cached) return (memory[key] = cached);

	try {
		const res = await fetch(`https://www.alphavantage.co/query?function=${fn}&interval=${interval}&apikey=${OIL_DATA_API_KEY}`);
		const json = await res.json();
		if (!Array.isArray(json?.data)) throw new Error(json?.Note || json?.Information || "No data");
		const points = json.data
			.filter((p) => !isNaN(parseFloat(p.value)))
			.slice(0, 30)
			.map((p) => ({ date: p.date, value: parseFloat(p.value) }))
			.reverse();
		const series = { name: fn === "WTI" ? "WTI Crude" : "Brent Crude", unit: json.unit || "dollars per barrel", points, live: true };
		writeCache(key, series);
		return (memory[key] = series);
	} catch {
		// Rate-limited or offline: fall back to clearly-labelled sample data.
		return { ...(fn === "WTI" ? SAMPLE_WTI : SAMPLE_BRENT), live: false };
	}
}

export function seriesStats(series) {
	const pts = series?.points || [];
	if (pts.length === 0) return { last: null, change: 0, pct: 0 };
	const last = pts[pts.length - 1].value;
	const prev = pts.length > 1 ? pts[pts.length - 2].value : last;
	const change = last - prev;
	return { last, change, pct: prev ? (change / prev) * 100 : 0 };
}

// The backend's news feed passes RSS markup straight through (<a href>, <font>, &nbsp;)
export function stripHtml(html) {
	if (!html) return "";
	const doc = new DOMParser().parseFromString(html, "text/html");
	return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

// Google News RSS titles end with " - Source"
export function splitHeadline(item) {
	const title = stripHtml(item?.title);
	const idx = title.lastIndexOf(" - ");
	return idx > 0 ? { headline: title.slice(0, idx), source: title.slice(idx + 3) } : { headline: title, source: "" };
}

export function useOilPrices(interval = "daily") {
	const [state, setState] = useState({ loading: true, wti: null, brent: null });
	useEffect(() => {
		let alive = true;
		setState((s) => ({ ...s, loading: true }));
		Promise.all([fetchSeries("WTI", interval), fetchSeries("BRENT", interval)]).then(([wti, brent]) => {
			if (alive) setState({ loading: false, wti, brent });
		});
		return () => {
			alive = false;
		};
	}, [interval]);
	return state;
}

export function useNews() {
	const [state, setState] = useState({ loading: !memory.news, news: memory.news || [] });
	useEffect(() => {
		let alive = true;
		const cached = memory.news || readCache(NEWS_CACHE_KEY);
		if (cached) {
			memory.news = cached;
			setState({ loading: false, news: cached });
			return undefined;
		}
		getNews().then((res) => {
			const seen = new Set();
			const news = (res && !res.error && Array.isArray(res.data) ? res.data : []).filter((n) => {
				const k = n.newsUrl || n.title;
				if (!k || seen.has(k)) return false;
				seen.add(k);
				return true;
			});
			if (news.length) {
				memory.news = news;
				writeCache(NEWS_CACHE_KEY, news);
			}
			if (alive) setState({ loading: false, news });
		});
		return () => {
			alive = false;
		};
	}, []);
	return state;
}

const SAMPLE_WTI = {
	name: "WTI Crude",
	unit: "dollars per barrel",
	points: [79.62, 80.39, 77.45, 76.56, 75.6, 76.55, 74.68].map((value, i) => ({ date: `2023-03-0${i + 3}`, value })),
};
const SAMPLE_BRENT = {
	name: "Brent Crude",
	unit: "dollars per barrel",
	points: [82.59, 82.5, 80.92, 81.42, 83.45, 82.4, 83.4].map((value, i) => ({ date: `2023-03-0${i + 3}`, value })),
};
